import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Eye, FileText, Paperclip, Plus, Save, Trash2, Upload, X } from 'lucide-react'
import toast from 'react-hot-toast'

import { showDeleteConfirm, showSaveError, showSaveSuccess, showSuccess } from '@/lib/swal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { branchService } from '@/services/branchService'
import { inventoryMovementService } from '@/services/inventoryMovementService'
import { inventoryDispatchService } from '@/services/inventoryDispatchService'
import type { BranchEntity, ItemEntity, Warehouse } from '@/api/types'
import { config } from '@/core/config'

function resolveAttachmentUrl(pathOrUrl: string | null | undefined): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return encodeURI(raw)
  if (/^data:/i.test(raw)) return raw
  const normalizedPath = raw.replace(/\\/g, '/').replace(/^\/+/, '')
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  if (!base) return encodeURI(`/${normalizedPath}`)
  return encodeURI(`${base}/${normalizedPath}`)
}

import { getDispatch, upsertDispatch } from '../storage'
import type { InventoryDispatch, InventoryDispatchLine } from '../types'
import { newDispatchLine } from '../types'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function clampNonNegative(n: number) {
  return Number.isFinite(n) ? Math.max(0, n) : 0
}

/** `previousQuantity` = API raw available. Edit mode: show current as API + dispatch (add back this line's qty). */
function displayedCurrentQuantity(isNew: boolean, line: { previousQuantity?: number; quantityDispatched?: number; batchId?: string }) {
  if (!line.batchId) return 0
  const api = Number(line.previousQuantity || 0)
  const disp = Number(line.quantityDispatched || 0)
  return isNew ? api : api + disp
}

function balanceAfterDispatch(isNew: boolean, line: { previousQuantity?: number; quantityDispatched?: number }) {
  const api = Number(line.previousQuantity || 0)
  const disp = Number(line.quantityDispatched || 0)
  const display = isNew ? api : api + disp
  return clampNonNegative(display - disp)
}

type BatchOption = { id: number; code: string; reference: string; availableQuantity: number }

function ensureFiveLines(lines: InventoryDispatchLine[]) {
  if (lines.length >= 5) return lines
  return [...lines, ...Array.from({ length: 5 - lines.length }, () => newDispatchLine())]
}

/** Edit: only rows that were saved or partially filled — no blank padding rows. */
function linesWithDataForEdit(raw: InventoryDispatchLine[]): InventoryDispatchLine[] {
  return raw.filter(
    (l) =>
      String(l.itemId || '').trim() !== '' ||
      String(l.batchId || '').trim() !== '' ||
      Number(l.quantityDispatched || 0) > 0,
  )
}

function branchLabel(b: BranchEntity): string {
  const id = b.id ?? 0
  return String(b.nameEn || b.nameAr || b.shortName || id)
}

function branchNumericId(b: BranchEntity): number {
  const raw = (b as { branchId?: unknown }).branchId ?? (b as { BranchId?: unknown }).BranchId ?? b.id
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** Minimal warehouse row so Radix Select can show a value when the id is not in the loaded list. */
function warehouseFallbackRow(id: number, label: string): Warehouse {
  return {
    id,
    code: '',
    name: label,
    location: '',
    isTemperatureControlled: false,
    email: '',
    empId: 0,
    wareHouseManagerName: '',
    contactNumber: '',
    operationalHours: '',
    isWarehouseDistribution: false,
    branchId: 0,
    city: 0,
    country: 0,
  }
}

function mergeWarehouseById(list: Warehouse[], warehouseId: string, label: string): Warehouse[] {
  if (!warehouseId.trim()) return list
  if (list.some((w) => String(w.id) === String(warehouseId))) return list
  const id = Number(warehouseId)
  if (!Number.isFinite(id) || id <= 0) return list
  return [...list, warehouseFallbackRow(id, label)]
}

/** Server `Id` and line `transferId`: 0 on first save. Local `dispatchId` from Date.now() is not a server id. */
function apiMasterIdAndTransferId(isNew: boolean, dispatchIdStr: string): number {
  if (isNew) return 0
  const s = String(dispatchIdStr).trim()
  if (!/^\d+$/.test(s)) return 0
  const n = Number(s)
  if (!Number.isFinite(n) || n <= 0) return 0
  if (n > 1_000_000_000_000) return 0
  return n
}

function normalizeReceivingStatus(raw: unknown): 1 | 2 | 3 | 4 {
  const n = Number(raw)
  if (n === 1 || n === 2 || n === 3 || n === 4) return n
  return 1
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '')
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

type AttachmentFieldProps = {
  inputId: string
  mode: 'new' | 'edit'
  file: File | null
  previewUrl: string
  previewName: string
  hasExistingWithoutPreview: boolean
  existingName: string
  onFileChange: (file: File | null) => void
  t: (key: string, opts?: Record<string, unknown>) => string
}

/**
 * Single polished attachment control:
 * - "new" mode: just a clean drop-zone style button to pick a file.
 * - "edit" mode: shows the saved/picked file with a View eye link, plus
 *   Replace / Remove actions. The underlying `<input type="file">` stays
 *   hidden so the surrounding chrome looks consistent across browsers.
 */
const AttachmentField: React.FC<AttachmentFieldProps> = ({
  inputId,
  mode,
  file,
  previewUrl,
  previewName,
  hasExistingWithoutPreview,
  existingName,
  onFileChange,
  t,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const hasViewable = Boolean(previewUrl)
  const hasAnyAttachment = hasViewable || hasExistingWithoutPreview || Boolean(file)

  const openPicker = () => fileInputRef.current?.click()
  const clearFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    onFileChange(null)
  }

  const fileName =
    file?.name ||
    previewName ||
    existingName ||
    t('inventoryDispatch.noFileChosen', { defaultValue: 'No file chosen' })

  return (
    <div className="space-y-1">
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        className="sr-only"
        onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
        aria-label={t('inventoryDispatch.file')}
      />

      {mode === 'edit' && hasAnyAttachment ? (
        <div className="flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 transition-colors hover:border-primary/40 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20">
          <Paperclip className="h-4 w-4 shrink-0 text-primary" />
          {hasViewable ? (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate text-sm text-foreground hover:text-primary hover:underline"
              title={t('inventoryMovements.viewDocument', { defaultValue: 'View' })}
            >
              {fileName}
            </a>
          ) : (
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">{fileName}</span>
          )}
          <div className="flex shrink-0 items-center gap-1">
            {hasViewable ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
                title={t('inventoryMovements.viewDocument', { defaultValue: 'View' })}
              >
                <Eye className="h-4 w-4" />
              </Button>
            ) : null}
            <Button type="button" variant="ghost" size="sm" className="h-8 gap-1 px-2" onClick={openPicker}>
              <Upload className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">
                {t('inventoryDispatch.replace', { defaultValue: 'Replace' })}
              </span>
            </Button>
            {file ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                onClick={clearFile}
                title={t('common.clear', { defaultValue: 'Clear' })}
              >
                <X className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className="flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 text-left transition-colors hover:border-primary/40 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20"
        >
          <Upload className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm">
            {file ? (
              <span className="text-foreground">{file.name}</span>
            ) : (
              <span className="text-muted-foreground">
                {t('inventoryDispatch.chooseFile', { defaultValue: 'Choose a file to upload' })}
              </span>
            )}
          </span>
          {file ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                clearFile()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  clearFile()
                }
              }}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-destructive"
              title={t('common.clear', { defaultValue: 'Clear' })}
            >
              <X className="h-4 w-4" />
            </span>
          ) : null}
        </button>
      )}

      {mode === 'edit' && file ? (
        <p className="truncate text-xs text-emerald-600 dark:text-emerald-400">
          {t('inventoryDispatch.newFileSelected', { defaultValue: 'New file selected — will replace on save' })}
        </p>
      ) : mode === 'edit' && hasExistingWithoutPreview ? (
        <p className="truncate text-xs text-amber-600 dark:text-amber-400">
          {t('inventoryDispatch.reuploadToEnableView', {
            defaultValue: 'Re-upload the same file to enable preview.',
          })}
        </p>
      ) : null}
    </div>
  )
}

export const InventoryDispatchEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)

  const isNew = !id || id === 'new'
  const existing = useMemo(() => (!isNew && id ? getDispatch(id) : undefined), [id, isNew])

  const [dispatchDate, setDispatchDate] = useState(existing?.dispatchDate || todayIso())
  const [sourceWarehouseId, setSourceWarehouseId] = useState(existing?.sourceWarehouseId || '')
  const [destinationBranchId, setDestinationBranchId] = useState(existing?.destinationBranchId || '')
  const [destinationWarehouseId, setDestinationWarehouseId] = useState(existing?.destinationWarehouseId || '')
  const [remarks, setRemarks] = useState(existing?.remarks || '')
  const [receivingStatus, setReceivingStatus] = useState(() => normalizeReceivingStatus(existing?.receivingStatus))
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  /** Existing attachment (loaded from local storage when editing) — kept
   *  separately from the newly-chosen file so the user can still view it
   *  while picking a replacement. */
  const [existingAttachmentName, setExistingAttachmentName] = useState<string>(existing?.attachmentName || '')
  const [existingAttachmentDataUrl, setExistingAttachmentDataUrl] = useState<string>(existing?.attachmentDataUrl || '')
  /** Data URL for the newly chosen file (built on the client) — lets us
   *  show a View button as soon as the user picks a file, before saving. */
  const [newAttachmentDataUrl, setNewAttachmentDataUrl] = useState<string>('')
  /** Server-resolved attachment file path returned by GetById — preferred
   *  source for the View link in edit mode (matches movements behaviour). */
  const [serverAttachmentPath, setServerAttachmentPath] = useState<string>('')
  const serverAttachmentUrl = useMemo(
    () => resolveAttachmentUrl(serverAttachmentPath),
    [serverAttachmentPath],
  )

  /** Resolved preview URL/name shown next to the file input. Priority:
   *  1. Newly picked file (immediate preview)
   *  2. Server-provided attachment from GetById
   *  3. Locally cached data URL (older records saved client-side)
   */
  const attachmentPreviewUrl = attachmentFile
    ? newAttachmentDataUrl
    : !isNew
      ? serverAttachmentUrl || existingAttachmentDataUrl
      : ''
  const attachmentPreviewName = attachmentFile
    ? attachmentFile.name
    : !isNew
      ? existingAttachmentName ||
        (serverAttachmentPath ? serverAttachmentPath.split(/[\\/]/).pop() || '' : '')
      : ''
  const [lines, setLines] = useState<InventoryDispatchLine[]>(() =>
    isNew ? ensureFiveLines(existing?.lines || []) : linesWithDataForEdit(existing?.lines || []),
  )

  const [branches, setBranches] = useState<BranchEntity[]>([])
  const [loadingBranches, setLoadingBranches] = useState(false)
  const [sourceWarehouses, setSourceWarehouses] = useState<Warehouse[]>([])
  const [loadingSourceWarehouses, setLoadingSourceWarehouses] = useState(false)
  const [destinationWarehouses, setDestinationWarehouses] = useState<Warehouse[]>([])
  const [loadingDestinationWarehouses, setLoadingDestinationWarehouses] = useState(false)
  /** When editing, API master branch for the transfer source (may differ from header branch). */
  const [editSourceBranchId, setEditSourceBranchId] = useState(0)
  const [isSaving, setIsSaving] = useState(false)

  const [itemEntities, setItemEntities] = useState<ItemEntity[]>([])
  const [batchOptionsByLineId, setBatchOptionsByLineId] = useState<Record<string, BatchOption[]>>({})
  const batchesCacheRef = useRef<Record<string, BatchOption[]>>({})
  const existingBatchesLoadedRef = useRef(false)

  const code = existing?.code || (isNew ? `DISP-${new Date().getTime()}` : '')
  const dispatchId = existing?.id || (isNew ? `${Date.now()}` : String(id))

  const srcWhNum = Number(sourceWarehouseId || 0)

  const sourceListBranchId = isNew ? selectedBranchId : editSourceBranchId || selectedBranchId

  const branchSelectOptions = useMemo(() => {
    const out: { id: number; label: string }[] = []
    for (const b of branches) {
      const id = branchNumericId(b)
      if (!id) continue
      out.push({ id, label: branchLabel({ ...b, id }) })
    }
    return out
  }, [branches])

  const sourceWarehouseOptions = useMemo(() => {
    const label = t('inventoryDispatch.warehouseIdFallback', { id: sourceWarehouseId || '—' })
    return mergeWarehouseById(sourceWarehouses, String(sourceWarehouseId || ''), label)
  }, [sourceWarehouses, sourceWarehouseId, t])

  const destinationWarehouseOptions = useMemo(() => {
    const label = t('inventoryDispatch.warehouseIdFallback', { id: destinationWarehouseId || '—' })
    return mergeWarehouseById(destinationWarehouses, String(destinationWarehouseId || ''), label)
  }, [destinationWarehouses, destinationWarehouseId, t])

  const destinationBranchSelectOptions = useMemo(() => {
    const bid = Number(destinationBranchId || 0)
    const opts = [...branchSelectOptions]
    if (bid > 0 && !opts.some((o) => o.id === bid)) {
      opts.push({
        id: bid,
        label: t('inventoryDispatch.branchIdFallback', { id: bid }),
      })
    }
    return opts
  }, [branchSelectOptions, destinationBranchId, t])

  const itemOptions = useMemo(() => {
    const base = itemEntities.map((it) => {
      const name = language === 'ar' ? it.itemNameAr || it.itemNameEn || it.itemCode : it.itemNameEn || it.itemNameAr || it.itemCode
      return {
        value: String(it.itemId),
        label: `${it.itemCode || ''}${it.itemCode ? ' — ' : ''}${name || ''}`.trim(),
      }
    })
    const seen = new Set(base.map((o) => o.value))
    const extras: { value: string; label: string }[] = []
    for (const l of lines) {
      const id = String(l.itemId || '').trim()
      if (!id || seen.has(id)) continue
      seen.add(id)
      extras.push({
        value: id,
        label: t('inventoryDispatch.itemIdFallback', { id }),
      })
    }
    return [...base, ...extras]
  }, [itemEntities, language, lines, t])

  // Edit mode: load the saved master from the server so we can show the
  // attachment "View" link (and any updated fields) — same UX as movements.
  // We try whenever the URL id looks like a server integer id; if the server
  // doesn't recognise it we silently fall back to the local-storage record.
  useEffect(() => {
    if (isNew) return
    const idStr = String(id ?? '').trim()
    if (!/^\d+$/.test(idStr)) return
    const idNum = Number(idStr)
    if (!Number.isFinite(idNum) || idNum <= 0) return

    let cancelled = false
    void (async () => {
      const master = await inventoryDispatchService.getById(idNum)
      if (cancelled || !master) return

      const masterSourceBranch = Number(master.sourceBranchId ?? master.branchId ?? 0)
      if (masterSourceBranch > 0) setEditSourceBranchId(masterSourceBranch)

      setServerAttachmentPath(master.attachmentFilePath || '')
      if (master.movementDate) {
        setDispatchDate(String(master.movementDate).slice(0, 10))
      }
      if (master.sourceWarehouseId) setSourceWarehouseId(String(master.sourceWarehouseId))
      if (master.destinationBranchId) setDestinationBranchId(String(master.destinationBranchId))
      if (master.destinationWarehouseId) setDestinationWarehouseId(String(master.destinationWarehouseId))
      if (master.remarks) setRemarks(master.remarks)
      if (master.receivingStatus) setReceivingStatus(normalizeReceivingStatus(master.receivingStatus))

      try {
        const apiLines = await inventoryDispatchService.getLinesByTransferId(idNum)
        if (cancelled) return
        if (apiLines.length > 0) {
          const mapped: InventoryDispatchLine[] = apiLines.map((l) => {
            const previous = Number(l.previousQuantity || 0)
            const qty = Number(l.quantityTransfered || 0)
            return {
              id: `srv-${l.id || `${l.itemId}-${l.batchId}`}`,
              itemId: l.itemId > 0 ? String(l.itemId) : '',
              batchId: l.batchId > 0 ? String(l.batchId) : '',
              previousQuantity: previous,
              quantityDispatched: qty,
              balanceQuantity: clampNonNegative(previous + qty - qty),
            }
          })
          setLines(mapped)
        }
      } catch (err) {
        console.warn('[InventoryDispatch] failed to load lines', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id, isNew])

  useEffect(() => {
    setEditSourceBranchId(0)
  }, [isNew, id])

  // Source warehouses: current branch (new) or transfer source branch from API (edit)
  useEffect(() => {
    if (!sourceListBranchId) {
      setSourceWarehouses([])
      setLoadingSourceWarehouses(false)
      return
    }
    let cancelled = false
    setLoadingSourceWarehouses(true)
    void inventoryMovementService
      .getWarehousesByBranchIdTransferBranch(sourceListBranchId)
      .then((list) => {
        if (!cancelled) setSourceWarehouses(list)
      })
      .catch(() => {
        if (!cancelled) {
          setSourceWarehouses([])
          toast.error(t('inventoryDispatch.failedLoadWarehouses'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSourceWarehouses(false)
      })
    return () => {
      cancelled = true
    }
  }, [sourceListBranchId, t])

  // Items for line grid: source warehouse
  useEffect(() => {
    if (!srcWhNum) {
      setItemEntities([])
      return
    }
    let cancelled = false
    void inventoryMovementService
      .getItemsByWarehouseTransferBranch(srcWhNum)
      .then((items) => {
        if (!cancelled) setItemEntities(items)
      })
      .catch(() => {
        if (!cancelled) {
          setItemEntities([])
          toast.error(t('inventoryDispatch.failedLoadItems'))
        }
      })
    return () => {
      cancelled = true
    }
  }, [srcWhNum, t])

  // Branch list for destination branch dropdown
  useEffect(() => {
    let cancelled = false
    setLoadingBranches(true)
    void branchService
      .getAllBranches()
      .then((list) => {
        if (!cancelled) setBranches(list)
      })
      .catch(() => {
        if (!cancelled) setBranches([])
      })
      .finally(() => {
        if (!cancelled) setLoadingBranches(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Destination warehouses: branch chosen in "Destination branch"
  useEffect(() => {
    const bid = Number(destinationBranchId || 0)
    if (!bid) {
      setDestinationWarehouses([])
      setLoadingDestinationWarehouses(false)
      return
    }
    let cancelled = false
    setLoadingDestinationWarehouses(true)
    void inventoryMovementService
      .getWarehousesByBranchIdTransferBranch(bid)
      .then((list) => {
        if (!cancelled) setDestinationWarehouses(list)
      })
      .catch(() => {
        if (!cancelled) {
          setDestinationWarehouses([])
          toast.error(t('inventoryDispatch.failedLoadWarehouses'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDestinationWarehouses(false)
      })
    return () => {
      cancelled = true
    }
  }, [destinationBranchId, t])

  useEffect(() => {
    existingBatchesLoadedRef.current = false
    batchesCacheRef.current = {}
  }, [sourceWarehouseId])

  // Edit (local): preload batch dropdowns for lines that already have itemId
  useEffect(() => {
    if (isNew) return
    if (existingBatchesLoadedRef.current) return
    if (!srcWhNum) return
    if (!lines.some((l) => l.itemId)) return

    existingBatchesLoadedRef.current = true

    void (async () => {
      try {
        const uniqueItemIds = Array.from(new Set(lines.map((l) => l.itemId).filter(Boolean))).map((s) => Number(s))
        const loaded: Record<number, BatchOption[]> = {}
        await Promise.all(
          uniqueItemIds.map(async (itemIdNum) => {
            const key = `${srcWhNum}-${itemIdNum}`
            if (batchesCacheRef.current[key]) {
              loaded[itemIdNum] = batchesCacheRef.current[key]
              return
            }
            const batches = await inventoryMovementService.getBatchesByWarehouseAndItemTransferBranch(srcWhNum, itemIdNum)
            batchesCacheRef.current[key] = batches
            loaded[itemIdNum] = batches
          }),
        )
        const nextByLine: Record<string, BatchOption[]> = {}
        for (const l of lines) {
          if (!l.itemId) continue
          nextByLine[l.id] = loaded[Number(l.itemId)] || []
        }
        setBatchOptionsByLineId((prev) => ({ ...prev, ...nextByLine }))

        for (let i = 0; i < lines.length; i++) {
          const l = lines[i]
          if (!l.itemId || !l.batchId) continue
          try {
            const details = await inventoryMovementService.getItemDetailsTransferBranch(srcWhNum, Number(l.itemId), Number(l.batchId))
            const avail = Number(details.availableQuantity ?? 0)
            const qty = Number(l.quantityDispatched || 0)
            setLines((prev) => {
              const next = [...prev]
              if (next[i]) {
                const row = { ...next[i], previousQuantity: avail, quantityDispatched: qty }
                next[i] = { ...row, balanceQuantity: balanceAfterDispatch(isNew, row) }
              }
              return next
            })
          } catch {
            // keep stored quantities
          }
        }
      } catch {
        existingBatchesLoadedRef.current = false
      }
    })()
  }, [isNew, lines, srcWhNum])

  const handleDestinationBranchChange = (value: string) => {
    setDestinationBranchId(value)
    setDestinationWarehouseId('')
  }

  const handleSourceWarehouseChange = (v: string) => {
    setSourceWarehouseId(v)
    setBatchOptionsByLineId({})
    batchesCacheRef.current = {}
    existingBatchesLoadedRef.current = false
    setLines(ensureFiveLines([]))
  }

  const addLines = (n: number) => setLines((prev) => [...prev, ...Array.from({ length: n }, () => newDispatchLine())])
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index))
  const updateLine = (index: number, patch: Partial<InventoryDispatchLine>) => {
    setLines((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...patch }
      return next
    })
  }

  const loadBatchesForItem = async (whId: number, itemIdNum: number): Promise<BatchOption[]> => {
    const key = `${whId}-${itemIdNum}`
    if (batchesCacheRef.current[key]) return batchesCacheRef.current[key]
    const batches = await inventoryMovementService.getBatchesByWarehouseAndItemTransferBranch(whId, itemIdNum)
    batchesCacheRef.current[key] = batches
    return batches
  }

  const handleItemChange = async (index: number, value: string) => {
    const line = lines[index]
    if (!line) return

    updateLine(index, {
      itemId: value,
      batchId: '',
      previousQuantity: 0,
      quantityDispatched: 0,
      balanceQuantity: 0,
    })

    const itemIdNum = Number(value || 0)
    if (!srcWhNum || !itemIdNum) {
      setBatchOptionsByLineId((prev) => ({ ...prev, [line.id]: [] }))
      return
    }

    try {
      const batches = await loadBatchesForItem(srcWhNum, itemIdNum)
      setBatchOptionsByLineId((prev) => ({ ...prev, [line.id]: batches }))
    } catch {
      setBatchOptionsByLineId((prev) => ({ ...prev, [line.id]: [] }))
    }
  }

  const handleBatchChange = async (index: number, value: string) => {
    const line = lines[index]
    if (!line) return
    let availableQuantity = 0
    try {
      if (!srcWhNum || !line.itemId) throw new Error('missing params')
      const details = await inventoryMovementService.getItemDetailsTransferBranch(srcWhNum, Number(line.itemId), Number(value))
      availableQuantity = Number(details.availableQuantity ?? 0)
    } catch {
      availableQuantity = 0
    }

    const prevTransferQty = Number(line.quantityDispatched ?? 0)
    const nextRow = { ...line, batchId: value, previousQuantity: availableQuantity, quantityDispatched: prevTransferQty }
    const nextBalance = balanceAfterDispatch(isNew, nextRow)

    updateLine(index, { batchId: value, previousQuantity: availableQuantity, balanceQuantity: nextBalance })
  }

  const handleQtyChange = (index: number, raw: string) => {
    const qty = Number(raw)
    const line = lines[index]
    if (!line) return
    const q = Number.isFinite(qty) ? qty : 0
    const nextRow = { ...line, quantityDispatched: q }
    const nextBalance = balanceAfterDispatch(isNew, nextRow)

    updateLine(index, {
      quantityDispatched: q,
      balanceQuantity: nextBalance,
    })
  }

  const handleBack = () => navigate('/inventory/dispatch')

  const handleDeleteDispatch = async () => {
    const apiId = Number(id)
    if (!apiId) {
      toast.error(t('inventoryDispatch.deleteFailed', { defaultValue: 'Failed to delete inventory dispatch.' }))
      return
    }
    const label = (existing?.code || String(id) || '').trim() || `Dispatch #${apiId}`
    const ok = await showDeleteConfirm(label)
    if (!ok) return
    setIsSaving(true)
    try {
      await inventoryDispatchService.delete(apiId)
      await showSuccess(
        t('inventoryDispatch.deleteSuccess', { defaultValue: 'Inventory dispatch deleted.' }),
        t('inventoryDispatch.deleteSuccess', { defaultValue: 'Inventory dispatch deleted.' }),
      )
      navigate('/inventory/dispatch')
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('inventoryDispatch.deleteFailed', { defaultValue: 'Failed to delete inventory dispatch.' })
      await showSaveError(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isNew) return

    if (!selectedBranchId) {
      toast.error(t('auth.branchSelectionRequired', { defaultValue: 'Please select a branch.' }))
      return
    }
    if (!sourceWarehouseId || !destinationWarehouseId) {
      toast.error(t('inventoryDispatch.validationWarehouses'))
      return
    }
    if (!destinationBranchId) {
      toast.error(t('inventoryDispatch.validationDestinationBranch'))
      return
    }
    if (sourceWarehouseId === destinationWarehouseId) {
      toast.error(t('inventoryDispatch.validationDifferentWarehouses'))
      return
    }

    const filled = lines.filter((l) => l.itemId && l.batchId && Number(l.quantityDispatched || 0) > 0)
    if (filled.length === 0) {
      toast.error(t('inventoryDispatch.validationLines'))
      return
    }

    const srcWh = sourceWarehouses.find((w) => String(w.id) === String(sourceWarehouseId))
    const destWh = destinationWarehouses.find((w) => String(w.id) === String(destinationWarehouseId))
    const destWhNum = Number(destinationWarehouseId || 0)

    const masterIdNum = apiMasterIdAndTransferId(isNew, dispatchId)

    const itemList = filled.map((l) => ({
      id: /^\d+$/.test(String(l.id)) ? Number(l.id) : 0,
      transferId: masterIdNum,
      itemId: Number(l.itemId || 0),
      batchId: Number(l.batchId || 0),
      fromWarehouseId: srcWhNum,
      toWarehouseId: destWhNum,
      previousQuantity: Number(l.previousQuantity || 0),
      quantityTransfered: Number(l.quantityDispatched || 0),
      newQuantity: Number(l.balanceQuantity || 0),
      isDeleted: 'false',
    }))

    const apiPayload = {
      id: masterIdNum,
      code,
      sourceWarehouseId: srcWhNum,
      sourceBranchId: isNew ? selectedBranchId : editSourceBranchId || selectedBranchId,
      destinationWarehouseId: destWhNum,
      destinationBranchId: Number(destinationBranchId || 0),
      movementDate: dispatchDate,
      movementType: 0,
      approvedManager: '',
      remarks,
      attachmentFilePath: '',
      branchId: selectedBranchId,
      receivingStatus,
      attachmentFile: attachmentFile,
      itemList,
    }

    console.log('[InventoryDispatch] Save — full API payload (before FormData)', apiPayload)

    // Capture the attachment so it can be re-opened from the edit screen
    // even after a refresh. If no new file was picked, keep the previously
    // stored data URL (set when the record was originally saved).
    let attachmentDataUrl = existingAttachmentDataUrl
    let attachmentName = existingAttachmentName
    if (attachmentFile) {
      try {
        attachmentDataUrl = await readFileAsDataUrl(attachmentFile)
        attachmentName = attachmentFile.name
      } catch (err) {
        console.warn('[InventoryDispatch] could not read attachment for local preview', err)
      }
    }

    const localPayload: InventoryDispatch = {
      id: dispatchId,
      code,
      dispatchDate,
      sourceWarehouseId,
      destinationWarehouseId,
      destinationBranchId,
      sourceWarehouseName: srcWh ? `${srcWh.code ? `${srcWh.code} — ` : ''}${srcWh.name || ''}`.trim() : undefined,
      destinationWarehouseName: destWh ? `${destWh.code ? `${destWh.code} — ` : ''}${destWh.name || ''}`.trim() : undefined,
      remarks,
      receivingStatus,
      attachmentName: attachmentName || undefined,
      attachmentDataUrl: attachmentDataUrl || undefined,
      lines: filled.map((l) => ({
        ...l,
        previousQuantity: Number(l.previousQuantity || 0),
        quantityDispatched: Number(l.quantityDispatched || 0),
        balanceQuantity: Number(l.balanceQuantity || 0),
      })),
    }

    try {
      setIsSaving(true)
      await inventoryDispatchService.save(apiPayload)
      upsertDispatch(localPayload)
      // Reflect newly saved attachment in local state so a subsequent
      // navigation back here (or staying on the page) shows the View button.
      setExistingAttachmentName(attachmentName || '')
      setExistingAttachmentDataUrl(attachmentDataUrl || '')
      setAttachmentFile(null)
      await showSaveSuccess(!isNew, t('inventoryDispatch.saveSuccess'))
      navigate('/inventory/dispatch')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('inventoryDispatch.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  const linesGridDisabled = !srcWhNum

  /** Existing record → render fields read-only (Status stays editable). */
  const viewOnly = !isNew
  const readonlyFieldClass = 'focus-visible:ring-0 focus-visible:ring-offset-0'

  const sourceWarehouseLabel = useMemo(() => {
    const w = sourceWarehouseOptions.find((o) => String(o.id) === String(sourceWarehouseId))
    if (!w) return ''
    return w.code ? `${w.code} — ${w.name || ''}` : w.name || w.code || ''
  }, [sourceWarehouseOptions, sourceWarehouseId])

  const destinationBranchLabel = useMemo(() => {
    const b = destinationBranchSelectOptions.find((o) => String(o.id) === String(destinationBranchId))
    return b?.label || ''
  }, [destinationBranchSelectOptions, destinationBranchId])

  const destinationWarehouseLabel = useMemo(() => {
    const w = destinationWarehouseOptions.find((o) => String(o.id) === String(destinationWarehouseId))
    if (!w) return ''
    return w.code ? `${w.code} — ${w.name || ''}` : w.name || w.code || ''
  }, [destinationWarehouseOptions, destinationWarehouseId])

  const itemLabelById = useMemo(() => {
    const map = new Map<string, string>()
    for (const o of itemOptions) map.set(o.value, o.label)
    return map
  }, [itemOptions])

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryDispatch.formTitle')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryDispatch.formSubtitle')}</p>
          </div>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          void handleSubmit(e)
        }}
        className="space-y-6"
      >
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('inventoryDispatch.sectionDispatchDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="dispatch-date">{t('inventoryDispatch.dispatchDate')}</Label>
              <Input
                id="dispatch-date"
                type="date"
                value={dispatchDate}
                onChange={(e) => setDispatchDate(e.target.value)}
                readOnly={viewOnly}
                className={viewOnly ? readonlyFieldClass : undefined}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryDispatch.sourceWarehouse')}</Label>
              {viewOnly ? (
                <Input readOnly value={sourceWarehouseLabel} className={readonlyFieldClass} />
              ) : (
                <Select
                  value={sourceWarehouseId || undefined}
                  onValueChange={handleSourceWarehouseChange}
                  disabled={loadingSourceWarehouses || !sourceListBranchId}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        loadingSourceWarehouses
                          ? t('common.loading', { defaultValue: 'Loading...' })
                          : t('inventoryDispatch.selectSource')
                      }
                    />
                  </SelectTrigger>
                  <SelectContent searchable>
                    {sourceWarehouseOptions.map((w) => (
                      <SelectItem key={w.id} value={String(w.id)}>
                        {w.code ? `${w.code} — ${w.name || ''}` : w.name || w.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryDispatch.destinationBranch')}</Label>
              {viewOnly ? (
                <Input readOnly value={destinationBranchLabel} className={readonlyFieldClass} />
              ) : (
                <Select
                  value={destinationBranchId || undefined}
                  onValueChange={handleDestinationBranchChange}
                  disabled={loadingBranches}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        loadingBranches
                          ? t('common.loading', { defaultValue: 'Loading...' })
                          : t('inventoryDispatch.selectDestinationBranch')
                      }
                    />
                  </SelectTrigger>
                  <SelectContent searchable>
                    {destinationBranchSelectOptions.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryDispatch.destinationWarehouse')}</Label>
              {viewOnly ? (
                <Input readOnly value={destinationWarehouseLabel} className={readonlyFieldClass} />
              ) : (
                <Select
                  value={destinationWarehouseId || undefined}
                  onValueChange={setDestinationWarehouseId}
                  disabled={!destinationBranchId || loadingDestinationWarehouses}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        loadingDestinationWarehouses
                          ? t('common.loading', { defaultValue: 'Loading...' })
                          : t('inventoryDispatch.selectDestination')
                      }
                    />
                  </SelectTrigger>
                  <SelectContent searchable>
                    {destinationWarehouseOptions
                      .filter((w) => String(w.id) !== String(sourceWarehouseId))
                      .map((w) => (
                        <SelectItem key={w.id} value={String(w.id)}>
                          {w.code ? `${w.code} — ${w.name || ''}` : w.name || w.code}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="dispatch-attachment">{t('inventoryDispatch.file')}</Label>
              <AttachmentField
                inputId="dispatch-attachment"
                mode={isNew ? 'new' : 'edit'}
                file={attachmentFile}
                previewUrl={attachmentPreviewUrl}
                previewName={attachmentPreviewName}
                hasExistingWithoutPreview={!attachmentPreviewUrl && !isNew && !!existingAttachmentName}
                existingName={existingAttachmentName}
                onFileChange={(file) => {
                  setAttachmentFile(file)
                  if (file) {
                    void readFileAsDataUrl(file)
                      .then((dataUrl) => setNewAttachmentDataUrl(dataUrl))
                      .catch(() => setNewAttachmentDataUrl(''))
                  } else {
                    setNewAttachmentDataUrl('')
                  }
                }}
                t={t}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('inventoryDispatch.receivingStatus')}</Label>
              <Select value={String(receivingStatus)} onValueChange={(v) => setReceivingStatus(normalizeReceivingStatus(v))}>
                <SelectTrigger>
                  <SelectValue placeholder={t('inventoryDispatch.selectReceivingStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('inventoryDispatch.receivingStatusPending')}</SelectItem>
                  <SelectItem value="2">{t('inventoryDispatch.receivingStatusTransferred')}</SelectItem>
                  <SelectItem value="3">{t('inventoryDispatch.receivingStatusReceived')}</SelectItem>
                  <SelectItem value="4">{t('inventoryDispatch.receivingStatusCancel')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="remarks">{t('inventoryDispatch.remarks')}</Label>
              <Input
                id="remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                readOnly={viewOnly}
                className={viewOnly ? readonlyFieldClass : undefined}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">{t('inventoryDispatch.sectionDispatchedItems')}</CardTitle>
            {viewOnly ? null : (
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)} disabled={linesGridDisabled}>
                  <Plus className="mr-1 h-4 w-4" />
                  {t('inventoryDispatch.addRow')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)} disabled={linesGridDisabled}>
                  {t('inventoryDispatch.add3Rows')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)} disabled={linesGridDisabled}>
                  {t('inventoryDispatch.add5Rows')}
                </Button>
              </div>
            )}
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className={tableHeaderClass}>{t('inventoryDispatch.colItem')}</TableHead>
                    <TableHead className={tableHeaderClass}>{t('inventoryDispatch.colBatch')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>{t('inventoryDispatch.colCurrentQty')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>{t('inventoryDispatch.colQtyDispatch')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>{t('inventoryDispatch.colBalanceQty')}</TableHead>
                    {viewOnly ? null : (
                      <TableHead className={`${tableHeaderClass} w-[64px] text-center`}>{t('common.delete')}</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, index) => {
                    const batchOptionsRaw = batchOptionsByLineId[line.id] || []
                    const batchIdNum = Number(line.batchId || 0)
                    const batchInList =
                      batchIdNum > 0 && batchOptionsRaw.some((b) => String(b.id) === String(line.batchId))
                    const batchOptions =
                      batchIdNum > 0 && !batchInList
                        ? [
                            ...batchOptionsRaw,
                            {
                              id: batchIdNum,
                              code: String(batchIdNum),
                              reference: t('inventoryDispatch.batchIdFallback', { id: batchIdNum }),
                              availableQuantity: 0,
                            },
                          ]
                        : batchOptionsRaw
                    const itemLabel =
                      (line.itemId && itemLabelById.get(String(line.itemId))) ||
                      (line.itemId
                        ? t('inventoryDispatch.itemIdFallback', { id: line.itemId })
                        : '')
                    const batchSelected =
                      batchIdNum > 0
                        ? batchOptions.find((b) => String(b.id) === String(line.batchId))
                        : undefined
                    const batchLabel = batchSelected
                      ? batchSelected.code
                        ? `${batchSelected.code} — ${batchSelected.reference}`
                        : batchSelected.reference
                      : batchIdNum > 0
                        ? t('inventoryDispatch.batchIdFallback', { id: batchIdNum })
                        : ''
                    return (
                      <TableRow key={line.id}>
                        <TableCell className="align-top py-2">
                          {viewOnly ? (
                            <Input readOnly value={itemLabel} className={`h-9 ${readonlyFieldClass}`} />
                          ) : (
                            <Select
                              value={line.itemId || undefined}
                              onValueChange={(v) => void handleItemChange(index, v)}
                              disabled={linesGridDisabled}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue
                                  placeholder={
                                    linesGridDisabled
                                      ? t('inventoryDispatch.selectSourceWarehouseFirst')
                                      : t('inventoryDispatch.selectItem')
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent searchable>
                                {itemOptions.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>

                        <TableCell className="align-top py-2">
                          {viewOnly ? (
                            <Input readOnly value={batchLabel} className={`h-9 ${readonlyFieldClass}`} />
                          ) : (
                            <Select
                              value={line.batchId || undefined}
                              onValueChange={(v) => void handleBatchChange(index, v)}
                              disabled={!line.itemId}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder={t('inventoryDispatch.selectBatch')} />
                              </SelectTrigger>
                              <SelectContent searchable>
                                {batchOptions.map((b) => (
                                  <SelectItem key={b.id} value={String(b.id)}>
                                    {b.code ? `${b.code} — ${b.reference}` : b.reference}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>

                        <TableCell className="py-2 text-center align-top">
                          <Input
                            readOnly
                            className={`h-9 text-center ${viewOnly ? readonlyFieldClass : 'bg-muted/50'}`}
                            value={
                              line.itemId && line.batchId ? String(displayedCurrentQuantity(isNew, line)) : ''
                            }
                          />
                        </TableCell>

                        <TableCell className="py-2 text-center align-top">
                          <Input
                            type="number"
                            min={0}
                            className={`h-9 text-center ${viewOnly ? readonlyFieldClass : ''}`}
                            value={line.batchId ? String(line.quantityDispatched ?? 0) : ''}
                            onChange={(e) => handleQtyChange(index, e.target.value)}
                            disabled={!viewOnly && !line.batchId}
                            readOnly={viewOnly}
                          />
                        </TableCell>

                        <TableCell className="py-2 text-center align-top">
                          <Input
                            readOnly
                            className={`h-9 text-center ${viewOnly ? readonlyFieldClass : 'bg-muted/50'}`}
                            value={line.batchId ? String(line.balanceQuantity ?? 0) : ''}
                          />
                        </TableCell>

                        {viewOnly ? null : (
                          <TableCell className="py-2 text-center align-top">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => removeLine(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>
          {viewOnly ? (
            <Button
              type="button"
              variant="destructive"
              disabled={isSaving}
              onClick={() => void handleDeleteDispatch()}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('inventoryDispatch.delete', { defaultValue: 'Delete' })}
            </Button>
          ) : (
            <Button type="submit" disabled={isSaving}>
              <Save className="mr-2 h-4 w-4" />
              {t('inventoryDispatch.save')}
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}
